import { renderForm11 } from './pdf-form11-layout.js';
import type { Form11ClientParams } from './pdf-types.js';
import { formatPDFDate as formatDate, type InspectionRow } from './pdf-layout.js';
function formatThickness(value?: string): string {
  return !value ? '' : /mm$/i.test(value.trim()) ? value.trim() : `${value.trim()} mm`;
}
export function createForm11PDF(data: Form11ClientParams) {
  const { company } = data;
  const examDateStr = formatDate(data.examination_date);
  const rows: InspectionRow[] = [];
  const row = (number: string, label: string, value: string | string[], _minHeight?: number) => {
    rows.push({ number, label, value: Array.isArray(value) ? value.join('\n') : value });
  };
  // 16 Rows
  // Row 1: Occupier
  row('1', 'Occupier (Or Owner) Of Premises', company?.occupier_name ?? '', 5);

  // Row 2: Address
  const r2Str = [company?.company_name ?? '', company?.factory_address ?? ''].filter(Boolean).join('\n');
  row('2', 'Address', r2Str, 7);

  // Row 3: Name, description & distinctive number
  const p3Lines: string[] = [];
  if (data.vessel_name_description) p3Lines.push(data.vessel_name_description);
  const detailsParts: string[] = [];
  if (data.vessel_serial_no) detailsParts.push(`Sr.no.: ${data.vessel_serial_no}`);
  if (data.vessel_capacity) detailsParts.push(`Capacity: ${data.vessel_capacity}`);
  if (detailsParts.length) p3Lines.push(detailsParts.join(', '));
  if (data.vessel_model_no) p3Lines.push(`Model No: ${data.vessel_model_no}`);
  if (data.vessel_location) p3Lines.push(`Location: - ${data.vessel_location}`);
  if (data.vessel_description) p3Lines.push(data.vessel_description);
  row('3', 'Name, Description And Distinctive Number Of Pressure Vessel Or Plant', p3Lines.join('\n'), 8);

  // Row 4: Manufacture name & address
  row('4', 'Name And Address Of Manufacture', data.manufacturer_name_address || '', 5);

  // Row 5: Nature of process
  row('5', 'Nature Of Process (Including Temperature And Pressure Parameters)', data.nature_of_process || '', 5);

  // Row 6: Particulars of pressure vessel or plant
  const p6Lines = [
    `(A) ${formatDate(data.date_of_construction) || data.date_of_construction || ''}`,
    `(B) ${[
      data.thickness_shell ? `Shell: - ${formatThickness(data.thickness_shell)}` : '',
      data.thickness_dish ? `Dish: - ${formatThickness(data.thickness_dish)}` : '',
      data.thickness_top_dish ? `T-Dish: - ${formatThickness(data.thickness_top_dish)}` : '',
      data.thickness_bottom_dish ? `B-Dish: - ${formatThickness(data.thickness_bottom_dish)}` : '',
    ].filter(Boolean).join(' ')}`,
    `(C) ${formatDate(data.first_taken_into_use_date) || data.first_taken_into_use_date || ''}`,
    `(D) ${data.safe_working_pressure_recommended || ''}`,
  ];
  row(
    '6',
    'Particulars Of Pressure Vessel Or Plant-\n(A) Date Of Construction\n(B) Thickness Of Walls\n(C) Date On Which It Was First Taken Into Use\n(D) Safe Working Pressure Recommended By The Manufacturer',
    p6Lines,
    13,
  );

  // Row 7: Date of –
  const p7Lines = [
    `(A) ${formatDate(data.last_external_exam_date) || data.last_external_exam_date || ''}`,
    `(B) ${formatDate(data.last_internal_exam_date) || data.last_internal_exam_date || 'Inaccessible'}`,
    `(C) ${data.last_hydraulic_exam_details || 'First time by us'}`,
    `(D) ${formatDate(data.last_ultrasonic_exam_date) || data.last_ultrasonic_exam_date || ''}`,
  ];
  row(
    '7',
    'Date Of –\n(A) Last External Examination\n(B) Last Internal Examination\n(C) Last Hydraulic Examination\n(D) Last Ultrasonic or Other Non-destructive Test.',
    p7Lines,
    13,
  );

  // Row 8: Whether lagging was removed
  row('8', 'Whether Lagging Was Removed For Purposes Of Examination', data.lagging_removed_status || 'Non-Insulated Vessel', 5);

  // Row 9: Description of examinations carried out and findings
  const p9Lines = [
    `(A) ${data.exam_external_findings || `Good on ${examDateStr}`}`,
    `(B) ${data.exam_internal_findings || 'Inaccessible'}`,
    `(C) ${data.exam_hydraulic_findings || ''}`,
    `(D) ${data.exam_ultrasonic_findings || ''}`,
  ].filter(Boolean);
  row(
    '9',
    'Description Of Examinations Carried Out And Findings\n(A) External Examination (Give Reason If It Is Not Carried Out Six-monthly)\n(B) Internal Examination (Give Reasons If It Is Not Carried Out Annually)\n(C) Hydraulic Test(Give Reasons If This Is Not Carried Out At An Interval Of 2 Years Or 4 Years)\n(D) Ultrasonic Or Other Non-destructive Test.',
    p9Lines,
    15,
  );

  // Row 10: Condition of pressure plants
  const p10Lines = [
    `(A) ${data.condition_vessel || 'Good'}`,
    `(B) ${data.condition_piping || 'Good'}`,
  ];
  row('10', 'Condition Of Pressure Plants -\n(A) Vessel\n(B) Piping', p10Lines, 7);

  // Row 11: Conditions of fittings and appliances
  const p11Lines = [
    `(A) ${data.fittings_pressure_gauges || 'Good'}`,
    `(B) ${data.fittings_safety_valve || 'Good'}`,
    `(C) ${data.fittings_stop_valve || 'Good'}`,
    `(D) ${data.fittings_reducing_valve || 'Not Applicable'}`,
    `(E) ${data.fittings_additional_safety_valve || 'Not Applicable'}`,
    `(F) ${data.fittings_other_devices || 'Not Applicable'}`,
  ];
  row(
    '11',
    'Conditions Of Fittings And Appliances –\n(A) Pressure Gauges\n(B) Safety Valve\n(C) Stop Valve\n(D) Reducing Valve (Give Reasons If Not Necessary)\n(E) Additional Safety Valve (Required In Case A Reducing Valve Is Necessary)\n(F) Other Devices (Please Specify Particularly In Case Of Jacketed Vessels)',
    p11Lines,
    19,
  );

  // Row 12: Safe working pressure recommended after examination
  row(
    '12',
    'Safe Working Pressure Recommended After Examination (Specify The Allowances Made For Working Conditions Such As Heat, Corrosion, Etc.)',
    data.safe_working_pressure_recommended_after || '',
    5,
  );

  // Row 13: Repairs required
  const p13Lines = [
    `(A) ${data.repairs_required || 'NIL'}`,
    `(B) ${data.repairs_period || 'Not Applicable'}`,
    `(C) ${data.repairs_other_condition || 'NIL'}`,
  ];
  row(
    '13',
    '(A) Repairs (If Any) Required\n(B) Period Within Which The Repairs Should Be Executed.\n(C) Any Other Condition Which The Person Making The Examination Thinks It Necessary For Securing Safe Working.',
    p13Lines,
    10,
  );

  // Row 14: Specify reduced working pressure
  row('14', 'Specify Reduced Working Pressure Pending Repairs', data.reduced_working_pressure_pending_repairs || 'Not Required', 5);

  // Row 15: Safe working pressure calculated as per sub-rule 8
  row(
    '15',
    'Safe Working Pressure Calculated As Per: Methods Given In Sub-rule 8 For Thin-walled Pressure Vessel Or Plant',
    data.safe_working_pressure_subrule_8 || 'Not Applicable',
    5,
  );

  // Row 16: Other observations
  row('16', 'Other Observations.', data.other_observations || 'Safety valve operation and setting to be checked regularly.', 5.2);

  return renderForm11(data, rows,
    `I certify that on ${examDateStr} the pressure vessel / plant described above was thoroughly cleaned ` +
    `(so far as its construction permits) made accessible for thorough examination & for such tests as were necessary for ` +
    `thorough examination & that on the said date, I thoroughly examined this pressure vessel or plant, ` +
    `including its fittings and that the above is a true report of my examination.`);
}
